import React from 'react';
import HelpApiLayout from '../../../components/layout/HelpApiLayout';
import ApiSidebar from '../../../components/layout/ApiSidebar';
import ApiTester from '../components/ApiTester';
import EndpointCard from '../components/EndpointCard';
import CodeExample from '../components/CodeExample';
import './ApiDocsPage.css';

/**
 * Splits API Documentation Page
 */
const Splits = () => {
  return (
    <HelpApiLayout type="api" sidebar={<ApiSidebar />}>
      <div className="api-docs-page">
        <div className="api-docs-page-container with-tester">
          <main className="api-docs-main">
            <header className="api-docs-page-header">
              <h1 className="lp-section-title">Splits API</h1>
              <p className="lp-section-subtitle">
                Manage splits, expenses, and group payments through the Splits API.
              </p>
            </header>

            <section className="api-docs-page-section">
              <h2>External Partner Integration Endpoints</h2>
              <p>
                These endpoints are designed for third-party companies to integrate with WeSplit's split payment functionality.
                All endpoints require API key authentication via <code>Authorization: Bearer YOUR_API_KEY</code> header.
              </p>

              <div className="endpoints-list">
                <EndpointCard
                  method="POST"
                  path="/createSplitFromPayment"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/createSplitFromPayment"
                  description="Create a split from external payment/order data"
                  parameters={[
                    { name: 'email', type: 'string', description: 'User email address' },
                    { name: 'invoiceId', type: 'string', description: 'External invoice/order ID' },
                    { name: 'amount', type: 'number', description: 'Total amount' },
                    { name: 'currency', type: 'string', description: 'Currency code (e.g., USD)' },
                    { name: 'source', type: 'string', description: 'Your company name/identifier' },
                    { name: 'metadata', type: 'object', description: 'Optional metadata including webhookUrl, orderId, etc.' }
                  ]}
                  required={['email', 'invoiceId', 'amount', 'currency', 'source']}
                  curlExample={`curl -X POST "https://us-central1-wesplit-35186.cloudfunctions.net/createSplitFromPayment" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "invoiceId": "invoice_123",
    "amount": 100.0,
    "currency": "USD",
    "source": "your_company_name",
    "metadata": {
      "orderId": "order_123",
      "webhookUrl": "https://your-company.com/webhook",
      "webhookSecret": "webhook_secret",
      "paymentThreshold": 1.0,
      "callbackUrl": "https://your-company.com/callback"
    }
  }'`}
                  requestBody={{
                    email: "user@example.com",
                    invoiceId: "invoice_123",
                    amount: 100.0,
                    currency: "USD",
                    source: "your_company_name"
                  }}
                />

                <EndpointCard
                  method="POST"
                  path="/inviteParticipantsToSplit"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/inviteParticipantsToSplit"
                  description="Invite participants to an existing split"
                  parameters={[
                    { name: 'splitId', type: 'string', description: 'Split ID' },
                    { name: 'inviterId', type: 'string', description: 'User ID of the creator' },
                    { name: 'inviterName', type: 'string', description: 'Name of the inviter' },
                    { name: 'participants', type: 'array', description: 'Array of participant objects with email, name, amountOwed' }
                  ]}
                  required={['splitId', 'inviterId', 'inviterName', 'participants']}
                  curlExample={`curl -X POST "https://us-central1-wesplit-35186.cloudfunctions.net/inviteParticipantsToSplit" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "splitId": "split_123",
    "inviterId": "user_id",
    "inviterName": "John Doe",
    "participants": [
      {
        "email": "user1@example.com",
        "name": "User One",
        "amountOwed": 33.33
      }
    ]
  }'`}
                />

                <EndpointCard
                  method="POST"
                  path="/payParticipantShare"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/payParticipantShare"
                  description="Record a participant's payment for their share of the split"
                  parameters={[
                    { name: 'splitId', type: 'string', description: 'Split ID' },
                    { name: 'participantId', type: 'string', description: 'User ID of the payer' },
                    { name: 'amount', type: 'number', description: 'Payment amount' },
                    { name: 'currency', type: 'string', description: 'Currency code (e.g., USDC)' },
                    { name: 'transactionSignature', type: 'string', description: 'Optional transaction signature if payment already made on-chain' }
                  ]}
                  required={['splitId', 'participantId', 'amount', 'currency']}
                  curlExample={`curl -X POST "https://us-central1-wesplit-35186.cloudfunctions.net/payParticipantShare" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "splitId": "split_123",
    "participantId": "user_id",
    "amount": 33.33,
    "currency": "USDC"
  }'`}
                />

                <EndpointCard
                  method="GET"
                  path="/getSplitStatus"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/getSplitStatus"
                  description="Get current status, payment progress, and participant details for a split"
                  parameters={[
                    { name: 'splitId', type: 'string', description: 'Split ID (query parameter)' }
                  ]}
                  required={['splitId']}
                  curlExample={`curl -X GET "https://us-central1-wesplit-35186.cloudfunctions.net/getSplitStatus?splitId=split_123" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                />

                <EndpointCard
                  method="POST"
                  path="/batchInviteParticipants"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/batchInviteParticipants"
                  description="Batch invite multiple participants with automatic email notifications"
                  parameters={[
                    { name: 'splitId', type: 'string', description: 'Split ID' },
                    { name: 'inviterId', type: 'string', description: 'User ID of the creator' },
                    { name: 'inviterName', type: 'string', description: 'Name of the inviter' },
                    { name: 'participants', type: 'array', description: 'Array of participant objects' },
                    { name: 'sendNotifications', type: 'boolean', description: 'Whether to send email notifications' }
                  ]}
                  required={['splitId', 'inviterId', 'inviterName', 'participants']}
                  curlExample={`curl -X POST "https://us-central1-wesplit-35186.cloudfunctions.net/batchInviteParticipants" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "splitId": "split_123",
    "inviterId": "creator_user_id",
    "inviterName": "John Doe",
    "participants": [
      {
        "email": "user1@example.com",
        "name": "User One",
        "amountOwed": 33.33
      }
    ],
    "sendNotifications": true
  }'`}
                />

                <EndpointCard
                  method="GET"
                  path="/searchKnownUsers"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/searchKnownUsers"
                  description="Search for existing WeSplit users by email, name, or wallet address"
                  parameters={[
                    { name: 'query', type: 'string', description: 'Search term (min 2 characters, query parameter)' },
                    { name: 'limit', type: 'number', description: 'Maximum results (default: 20, max: 50, query parameter)' }
                  ]}
                  required={['query']}
                  curlExample={`curl -X GET "https://us-central1-wesplit-35186.cloudfunctions.net/searchKnownUsers?query=john&limit=20" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
                />

                <EndpointCard
                  method="POST"
                  path="/matchUsersByEmail"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/matchUsersByEmail"
                  description="Cross-reference a list of emails with WeSplit's user database to identify existing users vs. new users"
                  parameters={[
                    { name: 'emails', type: 'array', description: 'Array of email addresses to check' },
                    { name: 'splitId', type: 'string', description: 'Split ID' },
                    { name: 'creatorId', type: 'string', description: 'Creator user ID' }
                  ]}
                  required={['emails', 'splitId', 'creatorId']}
                  curlExample={`curl -X POST "https://us-central1-wesplit-35186.cloudfunctions.net/matchUsersByEmail" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "emails": ["user1@example.com", "user2@example.com"],
    "splitId": "split_123",
    "creatorId": "creator_user_id"
  }'`}
                />

                <EndpointCard
                  method="POST"
                  path="/spendWebhook"
                  fullUrl="https://us-central1-wesplit-35186.cloudfunctions.net/spendWebhook"
                  description="Receive webhook callbacks from external partners (order status updates, shipping notifications, etc.)"
                  parameters={[
                    { name: 'event', type: 'string', description: 'Event type (e.g., order.status_changed, order.shipped)' },
                    { name: 'order_id', type: 'string', description: 'Order ID' },
                    { name: 'status', type: 'string', description: 'Order status' },
                    { name: 'timestamp', type: 'string', description: 'ISO timestamp' },
                    { name: 'tracking_number', type: 'string', description: 'Optional tracking number' },
                    { name: 'tracking_url', type: 'string', description: 'Optional tracking URL' }
                  ]}
                  required={['event', 'order_id', 'status']}
                  note="Requires X-Spend-Signature header for HMAC-SHA256 signature verification"
                  curlExample={`curl -X POST "https://us-central1-wesplit-35186.cloudfunctions.net/spendWebhook" \\
  -H "X-Spend-Signature: t=1234567890,v1=signature_hash" \\
  -H "Content-Type: application/json" \\
  -d '{
    "event": "order.status_changed",
    "order_id": "order_123",
    "status": "shipped",
    "timestamp": "2024-01-01T10:00:00Z",
    "tracking_number": "TRACK123",
    "tracking_url": "https://tracking.example.com/TRACK123"
  }'`}
                />
              </div>
            </section>

            <section className="api-docs-page-section">
              <h2>Example: Create a Split from Payment</h2>
              <CodeExample
                code={`fetch('https://us-central1-wesplit-35186.cloudfunctions.net/createSplitFromPayment', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: 'user@example.com',
    invoiceId: 'invoice_123',
    amount: 100.0,
    currency: 'USD',
    source: 'your_company_name',
    metadata: {
      orderId: 'order_123',
      webhookUrl: 'https://your-company.com/webhook',
      webhookSecret: 'webhook_secret',
      paymentThreshold: 1.0,
      callbackUrl: 'https://your-company.com/callback'
    }
  })
})
.then(response => response.json())
.then(data => console.log(data));`}
                language="javascript"
                title="JavaScript"
              />
            </section>

            <section className="api-docs-page-section">
              <h2>Example: Get Split Status</h2>
              <CodeExample
                code={`fetch('https://us-central1-wesplit-35186.cloudfunctions.net/getSplitStatus?splitId=split_123', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data));`}
                language="javascript"
                title="JavaScript"
              />
            </section>
          </main>
          <aside className="api-tester-sidebar">
            <ApiTester
              endpoint="/getSplitStatus"
              method="GET"
              baseUrl="https://us-central1-wesplit-35186.cloudfunctions.net"
              defaultParams={{
                splitId: ''
              }}
              description="Test the getSplitStatus endpoint"
            />
          </aside>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default Splits;
